import { useState, useRef, useEffect } from 'react';
import { Bell, X, CheckCheck, Info, CreditCard, AlertTriangle, Clock, LogOut } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { Notification } from '@/api/types';

// ── API helpers ──────────────────────────────────────────────────────────────

async function fetchNotifications(): Promise<Notification[]> {
  const res = await client.get('/api/notifications?size=30') as { content: Notification[] };
  return res.content ?? [];
}

async function fetchUnreadCount(): Promise<number> {
  const res = await client.get('/api/notifications/unread-count') as { count: number };
  return res.count ?? 0;
}

// ── Icon by notification type ─────────────────────────────────────────────────

function TypeIcon({ type }: { type: string }) {
  const cls = 'shrink-0';
  if (type === 'PAYMENT_RECEIVED') return <CreditCard    size={15} className={`${cls} text-green-600`} />;
  if (type === 'RENT_REMINDER')    return <Clock         size={15} className={`${cls} text-amber-500`} />;
  if (type === 'OVERDUE')          return <AlertTriangle size={15} className={`${cls} text-red-500`} />;
  if (type === 'GENERAL_SENT')     return <CheckCheck    size={15} className={`${cls} text-teal-500`} />;
  if (type === 'TENANT_CHECKED_OUT') return <LogOut       size={15} className={`${cls} text-red-500`} />;
  return <Info size={15} className={`${cls} text-blue-500`} />;
}

// ── Toast ─────────────────────────────────────────────────────────────────────

export interface ToastMessage { id: number; title: string; body: string }

let _toastId = 0;

interface ToastProps { toasts: ToastMessage[]; onDismiss: (id: number) => void }

export function NotificationToasts({ toasts, onDismiss }: ToastProps) {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 w-80">
      {toasts.map(t => (
        <div
          key={t.id}
          className="bg-chrome-page border border-gray-200 rounded-lg shadow-lg p-3 flex gap-3 items-start animate-slide-in"
        >
          <Bell size={16} className="text-teal-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{t.title}</p>
            <p className="text-xs text-gray-500 line-clamp-2">{t.body}</p>
          </div>
          <button onClick={() => onDismiss(t.id)} className="text-gray-400 hover:text-gray-600">
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function useToasts() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  function addToast(title: string, body: string) {
    const id = ++_toastId;
    setToasts(prev => [...prev, { id, title, body }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }

  function dismissToast(id: number) {
    setToasts(prev => prev.filter(t => t.id !== id));
  }

  return { toasts, addToast, dismissToast };
}

// ── Bell + Dropdown ───────────────────────────────────────────────────────────

export function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  const { data: count = 0 } = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: fetchUnreadCount,
    refetchInterval: 30_000,
  });

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications-list'],
    queryFn: fetchNotifications,
    enabled: open,
  });

  const markAllRead = useMutation({
    mutationFn: () => client.post('/api/notifications/mark-all-read'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications-unread'] });
      qc.invalidateQueries({ queryKey: ['notifications-list'] });
    },
  });

  const markRead = useMutation({
    mutationFn: (id: string) => client.patch(`/api/notifications/${id}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications-unread'] });
      qc.invalidateQueries({ queryKey: ['notifications-list'] });
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  function toggle() {
    setOpen(v => !v);
  }

  function fmt(iso: string) {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1)  return t('notifications.justNow', 'just now');
    if (diffMin < 60) return t('notifications.minutesAgo', '{{count}}m ago', { count: diffMin });
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24)   return t('notifications.hoursAgo', '{{count}}h ago', { count: diffH });
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }

  return (
    <div ref={ref} className="relative">
      {/* Bell button */}
      <button
        onClick={toggle}
        className="relative p-1.5 rounded-lg hover:bg-gray-100 transition"
        aria-label={t('notifications.title', 'Notifications')}
      >
        <Bell size={20} className="text-gray-600" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-10 w-80 bg-chrome-page border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">{t('notifications.title', 'Notifications')}</span>
            {count > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 font-medium"
              >
                <CheckCheck size={13} />
                {t('notifications.markAllRead', 'Mark all read')}
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400">
                <Bell size={28} className="mx-auto mb-2 opacity-30" />
                {t('notifications.empty', 'No notifications yet')}
              </div>
            ) : (
              notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => { if (!n.isRead) markRead.mutate(n.id); }}
                  className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-gray-50 transition ${
                    n.isRead ? 'opacity-60' : ''
                  }`}
                >
                  <div className="mt-0.5">
                    <TypeIcon type={n.type} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm truncate ${n.isRead ? 'font-normal text-gray-700' : 'font-semibold text-gray-900'}`}>
                        {n.title}
                      </p>
                      {!n.isRead && (
                        <span className="w-2 h-2 rounded-full bg-teal-500 shrink-0 mt-1" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{n.message}</p>
                    <p className="text-[11px] text-gray-400 mt-1">{fmt(n.createdAt)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
